import {Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    UntypedFormBuilder,
    UntypedFormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR, ValidationErrors, FormGroup
} from "@angular/forms";
import {Observable, Subject, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {filter, map, pairwise} from "rxjs/operators";
import {isNotNullOrUndefined} from "../../providers/service/global-variables.service";

@Component({
    selector: 'app-increment-field',
    templateUrl: './increment-field.component.html',
    styleUrls: ['./increment-field.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: IncrementFieldComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: IncrementFieldComponent
        }
    ],
    standalone: false
})
export class IncrementFieldComponent implements OnChanges, OnDestroy, ControlValueAccessor {
    private subScriptions = new Subscription();
    public formGroup: FormGroup<any>;
    @Input() currentValue: number | undefined;
    @Input() min: number | undefined;
    @Input() max: number | undefined;
    @Input() step: number = 1;
    @Input() title: string | undefined;
    @Input() disabled: boolean | undefined;
    @Input() suffix: string | undefined;
    @Input() hintMessage: string | undefined;
    @Input() showClearButton: boolean = false;
    @Input() appearance: 'fill' | 'outline' = 'outline';
    @Input() floatLabel: 'always' | 'auto' = 'auto';
    @Input() marginTop= '21px';
    @Output() changeValue = new Subject();
    disableAdd$: Observable<boolean>
    disableRemove$: Observable<boolean>
    public focusOn = false

    constructor(private fb: UntypedFormBuilder,
                public translateService: TranslateService) {
        this.formGroup = this.fb.group({value: undefined});
        const sub = (this.formGroup as any).get('value').valueChanges.pipe(
            pairwise(),
            filter(([prev, current]) => prev != current),
            map(([prev, current]: [number, number]) => current),
            filter(() => !this.focusOn)
        )
            .subscribe((value: number) => {
                this.changeValue.next(value)
                this.setValueInLimit(value)
            });
        this.subScriptions.add(sub);
        this.disableAdd$ = (this.formGroup as any).get('value').valueChanges.pipe(
            map((value: number) => this.max != null && value >= this.max)
        )
        this.disableRemove$ = (this.formGroup as any).get('value').valueChanges.pipe(
            map((value: number) => {
                return this.min != null && value <= this.min
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentValue) {
            setTimeout(() => {
                this.focusOn = true;
                if (this.currentValue != null) {
                    (this.formGroup as any).get('value').setValue(this.currentValue);
                } else {
                    (this.formGroup as any).get('value').reset();
                }
                this.focusOn = false;
            })
        }
        if (changes.max) {
            const currentValue = (this.formGroup as any).get('value').value;
            if (this.max != null && currentValue != null && currentValue > this.max) {
                (this.formGroup as any).get('value').setValue(this.max);
            }
        }
        if (changes.min) {
            const currentValue = (this.formGroup as any).get('value').value;
            if (this.min != null && currentValue != null && currentValue < this.min) {
                (this.formGroup as any).get('value').setValue(this.min);
            }
        }
        if (changes.disabled) {
            if (this.disabled == true) {
                this.formGroup.disable();
            } else {
                this.formGroup.enable();
            }
        }
    }

    ngOnDestroy() {
        this.subScriptions.unsubscribe();

    }

    onTouched = () => {
    }


    registerOnChange(onChange: any) {
        const sub = (this.formGroup as any).get('value').valueChanges
            .pipe(
                filter((value: number) => {
                    let isValid = true
                    if (value != null) {
                        if (this.max != null && this.min != null) {
                            isValid = value >= this.min && value <= this.max
                            if (!isValid) {
                                if (value > this.max) {
                                    (this.formGroup as any).get('value').setValue(this.max)
                                } else if (value < this.min) {
                                    (this.formGroup as any).get('value').setValue(this.min)
                                }
                            }

                        } else if (this.min != null) {
                            isValid = value >= this.min
                            if (!isValid) {
                                (this.formGroup as any).get('value').setValue(this.min)
                            }
                        } else if (this.max != null) {
                            isValid = value <= this.max
                            if (!isValid) {
                                (this.formGroup as any).get('value').setValue(this.max)
                            }
                        }
                    }
                    if (isValid) {
                        this.changeValue.next((this.formGroup as any).get('value'))
                    }
                    return isValid
                })
            )
            .subscribe(onChange);
        this.subScriptions.add(sub);
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }


    setDisabledState(disabled: boolean) {
        if (disabled) {
            (this.formGroup as any).disable();
        } else {
            (this.formGroup as any).enable();
        }
    }


    writeValue(value: any) {
        if (!isNotNullOrUndefined(value)) {
            this.formGroup.reset();
        } else {
            (this.formGroup as any).get('value').setValue(value);
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!isNotNullOrUndefined((this.formGroup as any).get('value').validator)) {
            (this.formGroup as any).get('value').setValidators(control.validator);
        }
        if ((this.formGroup as any).get('value').valid) {
            return null;
        } else {
            return (this.formGroup as any).get('value').errors;
        }
    }

    private tenPower() {
        const stepString = this.step.toString();
        if (stepString.includes('.')) {
            const numberDigit = stepString.split('.')[1].length
            return Math.pow(10, numberDigit);
        } else {
            return 1
        }
    }

    private roundValue(value: number) {
        const tenPower = this.tenPower();
        return Math.round(value * tenPower) / tenPower;
    }

    clickSum() {
        if ((this.formGroup as any).get('value').enabled) {
            const currentValue = (this.formGroup as any).get('value').value;
            let value;
            if (this.max != null) {
                if ((currentValue + this.step) < this.max) {
                    const divisible = Math.floor((currentValue * this.tenPower()) / (this.step * this.tenPower())) + 1;
                    value = divisible * this.step;
                } else {
                    value = this.max;
                }
            } else {
                value = currentValue + this.step;
            }
            (this.formGroup as any).get('value').setValue(this.roundValue(value));
        }
    }

    clickRemove() {
        if ((this.formGroup as any).get('value').enabled) {
            const currentValue = (this.formGroup as any).get('value').value;
            let value;
            if (this.min != null) {
                if ((currentValue - this.step) > this.min) {
                    const divisible = Math.floor((currentValue * this.tenPower()) / (this.step * this.tenPower())) - 1;
                    value = divisible * this.step;
                } else {
                    value = this.min;
                }
            } else {
                value = currentValue - this.step;
            }
            (this.formGroup as any).get('value').setValue(this.roundValue(value));
        }
    }

    focusInInput() {
        this.focusOn = true;

    }

    setValueInLimit(currentValue: number) {
        if (currentValue != null) {
            if ((this.max != null && this.min != null) && (currentValue > this.max && currentValue < this.min)) {
                (this.formGroup as any).get('value').setValue(this.max)
            } else if (this.max != null && currentValue > this.max) {
                (this.formGroup as any).get('value').setValue(this.max)
            } else if (this.min != null && currentValue <= this.min) {
                (this.formGroup as any).get('value').setValue(this.min)
            } else {
                (this.formGroup as any).get('value').updateValueAndValidity()
                this.changeValue.next((this.formGroup as any).get('value').value);
            }
        }

        return (this.formGroup as any).get('value').value
    }

    focusOutInput() {
        this.focusOn = false;
        const currentValue = (this.formGroup as any).get('value').value;
        this.setValueInLimit(currentValue)
    }

    unset() {
        this.formGroup.reset()
    }
}
